import { useMemo } from 'react';
import type { StyleObject } from '@unifyapps/defs/types/styleObject';
import { useBlockAppearanceStyles } from '../useBlockAppearanceStyles';
import type { StyleResult } from '../useBlockAppearanceStyles/types';
import type { TextStylesAppearance } from './types';

export type UseTextAppearanceStylesParams = Partial<TextStylesAppearance>;

export interface UseTextAppearanceStylesReturn {
  textClassName: string;
  textStyle: StyleResult['style'];
}

export function useTextAppearanceStyles({
  fontFamily,
  fontStyle,
  textDecoration,
  textTransform,
  lineHeight,
  letterSpacing,
}: UseTextAppearanceStylesParams): UseTextAppearanceStylesReturn {
  const appearanceStyles = useMemo(() => {
    const next: StyleObject = {};

    if (fontFamily != null) next.fontFamily = fontFamily;
    if (fontStyle != null) next.fontStyle = fontStyle;
    if (textDecoration != null) next.textDecoration = textDecoration;
    // transform's "—" stores 'normal-case', but CSS needs 'none' — map it so "—" can reset/override
    // an inherited transform from a template.
    if (textTransform != null) {
      next.textTransform = textTransform === 'normal-case' ? 'none' : textTransform;
    }
    if (lineHeight != null) next.lineHeight = lineHeight;
    if (letterSpacing != null) next.letterSpacing = letterSpacing;

    return Object.keys(next).length > 0 ? next : undefined;
  }, [fontFamily, fontStyle, textDecoration, textTransform, lineHeight, letterSpacing]);

  const { className: textClassName, style: textStyle } = useBlockAppearanceStyles({
    appearanceStyles,
  });

  return { textClassName, textStyle };
}
